import { Api } from 'lib/api.mjs';

/** Campaigns and their ads — the only writes that reach live Meta spend. */

export function getCampaigns({ signal } = {}) {
  return Api.get('/campaigns', { signal });
}

/**
 * Today's figures for the same campaigns, for the Week/Today switch.
 *
 * A separate endpoint because the weekly payload is cached per week and this
 * is live. The server caches it for 60s with single-flight, so flipping the
 * switch back and forth does not hammer Meta.
 */
export function getTodayCampaigns({ signal } = {}) {
  return Api.get('/campaigns/today', { signal });
}

/**
 * The ads under one campaign, for the same period the list is showing.
 *
 * `view` is not optional in practice: the server defaults it to 'week', so
 * omitting it made an expanded campaign report the whole week's figures while
 * the row above it showed today's — the same ad, two different numbers, with
 * nothing saying why.
 *
 * `fresh` waits for a live read. A pause or a budget change passes it so the
 * previous status cannot paint back over the change.
 *
 * @param {{ campaignId: string, view?: 'week'|'today', signal?: AbortSignal, fresh?: boolean }} payload
 */
export function getCampaignAds({ campaignId, view = 'week', signal, fresh = false }) {
  const period = view === 'today' ? 'today' : 'week';
  const live = fresh ? '&fresh=1' : '';
  return Api.get(`/campaigns/${campaignId}/ads?view=${period}${live}`, { signal });
}

/**
 * Change a campaign's daily budget. This spends real money — the caller is
 * expected to have confirmed the figure first.
 *
 * `campaignName` and `oldBudget` are not decoration: the server writes them
 * into the activity log, which is how a budget change is later read back as
 * "X went from £100 to £150". Dropping them would leave an unattributable row.
 *
 * @param {{ campaignId: string, dailyBudget: number, campaignName: string,
 *           oldBudget?: string|null }} payload
 */
export function setCampaignBudget({ campaignId, dailyBudget, campaignName, oldBudget = null }) {
  return Api.post(`/campaigns/${campaignId}/budget`, {
    body: { dailyBudget, campaignName, oldBudget },
  });
}

/** @param {{ campaignId: string, status: string, campaignName: string }} payload */
export function toggleCampaign({ campaignId, status, campaignName }) {
  return Api.post(`/campaigns/${campaignId}/toggle`, { body: { status, campaignName } });
}

/** @param {{ adId: string, status: string, adName: string, campaignName?: string }} payload */
export function toggleAd({ adId, status, adName, campaignName }) {
  return Api.post(`/ads/${adId}/toggle`, { body: { status, adName, campaignName } });
}

/**
 * Delete one Meta ad. Permanent; the caller confirms first.
 * @param {{ adId: string, adName: string, campaignName?: string }} payload
 */
export function deleteAd({ adId, adName, campaignName }) {
  return Api.delete(`/ads/${adId}`, { body: { adName, campaignName } });
}

/**
 * Launch a DRAFT W campaign. A different operation from resuming a paused one:
 * it commits money for the first time and sends the campaign to moderation,
 * and it cannot be undone by launching again. The caller confirms first.
 *
 * @param {{ campaignId: string, campaignName: string }} payload
 */
export function launchCampaign({ campaignId, campaignName }) {
  return Api.post(`/campaigns/${campaignId}/launch`, { body: { campaignName } });
}

/**
 * One W campaign's ad groups, each with its ads — the drill-down Meta gets as
 * a flat ad list. Two levels, because W has two.
 *
 * `view` matches the Today / This Week switch for the same reason
 * getCampaignAds does: the tree under a row must cover the row's period.
 *
 * `fresh` waits for a live read, the same way the Meta ads list does after a pause.
 *
 * @param {{ campaignId: string, view?: 'week'|'today', signal?: AbortSignal, fresh?: boolean }} payload
 */
export function getWTree({ campaignId, view = 'week', signal, fresh = false }) {
  const period = view === 'today' ? 'today' : 'week';
  const live = fresh ? '&fresh=1' : '';
  return Api.get(`/w/campaigns/${campaignId}/tree?view=${period}${live}`, { signal });
}

/** @param {{ adGroupId: string, status: string, name: string }} payload */
export function toggleWAdGroup({ adGroupId, status, name }) {
  return Api.post(`/w/ad-groups/${adGroupId}/toggle`, { body: { status, name } });
}

/** @param {{ adId: string, status: string, name: string }} payload */
export function toggleWAd({ adId, status, name }) {
  return Api.post(`/w/ads/${adId}/toggle`, { body: { status, name } });
}

/**
 * The daily budget on ONE W ad group. Only where the group owns its budget —
 * a group whose CAMPAIGN owns it has no field to change, and the server
 * refuses rather than writing something the docs do not describe.
 *
 * @param {{ adGroupId: string, dailyBudget: number, name: string }} payload
 */
export function setWAdGroupBudget({ adGroupId, dailyBudget, name }) {
  return Api.post(`/w/ad-groups/${adGroupId}/budget`, { body: { dailyBudget, name } });
}

/**
 * Every W problem in one list — issues at all three levels, delivery states
 * that mean "not running", and writes that were refused.
 */
export function getWIssues({ signal } = {}) {
  return Api.get('/w/issues', { signal });
}

/**
 * Delete a W campaign, ad group or ad. Permanent: it archives the object on
 * the platform and a campaign cascades to its ad groups and ads. Same gate as
 * pause — the typed-title confirm in the UI is what stands between a click and
 * this call.
 *
 * `pauseFirst` is the operator having chosen "Pause and delete" on something
 * that is currently spending. Without it the server refuses that case rather
 * than pausing on their behalf.
 *
 * @param {{ level: 'campaign'|'ad_group'|'ad', id: string, pauseFirst?: boolean }} payload
 */
export function deleteWObject({ level, id, pauseFirst = false }) {
  const path = level === 'campaign' ? 'campaigns' : level === 'ad_group' ? 'ad-groups' : 'ads';
  return Api.delete(`/w/${path}/${id}`, { body: { pauseFirst } });
}

/**
 * Delete up to 25 W CAMPAIGNS in one batch. Sequential server-side; the answer
 * carries one outcome per id (deleted / paused_deleted / failed + message).
 */
export function bulkDeleteW({ ids }) {
  return Api.post('/w/campaigns/bulk-delete', { body: { ids } });
}

/**
 * Set a W ad group's bid strategy and/or its cost-per-result goal (dollars).
 * `bidType` omitted keeps the current strategy. Same gate as budget.
 */
export function setWAdGroupBid({ adGroupId, bidType = null, desiredCost = null, name, campaignName = null }) {
  return Api.post(`/w/ad-groups/${adGroupId}/bid`, { body: { bidType, desiredCost, name, campaignName } });
}

/**
 * One cost-per-result goal across every ACTIVE, target-bid ad group in a W
 * campaign — sequential server-side, per-group outcomes in the answer, groups
 * on Lowest cost skipped and named.
 */
export function setWCampaignGoal({ campaignId, desiredCost, campaignName }) {
  return Api.post(`/w/campaigns/${campaignId}/goal`, { body: { desiredCost, campaignName } });
}

/** Meta 🎯: one cost cap across a campaign's active capped ad sets, each read back. Same gate as Budget. */
export function setMetaCampaignGoal({ campaignId, goal, campaignName }) {
  return Api.post(`/meta/campaigns/${campaignId}/goal`, { body: { goal, campaignName } });
}

/** Meta 🎯 on one ad set (the ad set strip in the ads panel). */
export function setMetaAdsetGoal({ adsetId, campaignId, goal, campaignName }) {
  return Api.post(`/meta/adsets/${adsetId}/goal`, { body: { goal, campaignId, campaignName } });
}

/**
 * A Meta campaign's ad sets with their strategy and cap, validated server-side.
 * `fresh` waits for a live read after a cap change.
 */
export function getMetaAdsets({ campaignId, signal, fresh = false }) {
  const live = fresh ? '?fresh=1' : '';
  return Api.get(`/meta/campaigns/${campaignId}/adsets${live}`, { signal });
}

/** Retry the charge on a campaign W stopped for a failed payment. Same gate as pause. */
export function retryWPayment({ campaignId }) {
  return Api.post(`/w/campaigns/${campaignId}/retry-payment`, { body: {} });
}

/** The same, for a batch — sequential server-side, one Telegram for the lot. */
export function bulkRetryWPayments({ ids }) {
  return Api.post('/w/campaigns/bulk-retry-payment', { body: { ids } });
}
